import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import gsap from 'gsap';
import { useState, useCallback, useMemo, Fragment as Fragment$1 } from 'react';
import { shallow as shallow$1 } from '../../../node_modules/zustand/esm/shallow.js';
import { styled } from 'styled-components';
import { Link } from '@fabric-app/compat';
import useAlertStore from '../../store/alerts.js';
import { mediaHover, mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { ButtonIcon } from '@fabric-app/ui/components/Button';
import { IconMobileSheet24MiscCross } from '@fabric-app/icons/svg';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import dayjs from 'dayjs';
import plur from 'plur';
import { parseInlineMarkdown } from '@fabric-app/ui/markdown/markdown.utils';

const ActionButton = styled.button.withConfig({
  displayName: "BannerDisplay__ActionButton",
  componentId: "sc-85a6a28e-0"
})([
  `display:inline-flex;height:30px;padding:0px 8px;align-items:center;gap:8px;border-radius:8px;background:rgba(255,255,255,0.9);color:#666;font-family:Inter;font-size:13px;font-style:normal;font-weight:500;line-height:19px;flex-shrink:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`,
  `{&:hover{box-shadow:0 0 10rem 0 rgba(`,
  `,0.1) inset;}}`
], mediaHover, cssVar["color-bg-primary-reverse-rgb"]);
const BannerBadge = styled.span.withConfig({
  displayName: "BannerDisplay__BannerBadge",
  componentId: "sc-85a6a28e-1"
})([
  `display:inline-flex;padding:4px 8px;justify-content:center;align-items:center;gap:10px;border-radius:8px;background:rgba(0,0,0,0.22);color:#fff;text-align:center;font-family:Inter;font-size:15px;font-style:normal;font-weight:500;line-height:20px;[role='tooltip'] &{background:rgba(255,255,255,0.2);color:white;}`
]);
const Banner = styled.div.withConfig({
  displayName: "BannerDisplay__Banner",
  componentId: "sc-85a6a28e-2"
})([
  `position:relative;width:100%;margin-top:env(safe-area-inset-top);display:grid;grid-template-columns:1fr auto;padding:5px 6px;justify-content:center;align-items:center;gap:10px;align-self:stretch;color:#000;text-align:center;font-family:Inter;font-size:15px;font-style:normal;font-weight:500;> span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}background:#e2e2e2;&[data-severity='promotional']{background:#3200ff;color:white;}&[data-severity='light-informational']{background:#f3f3f3;color:black;`,
  `{background:rgba(0,0,0,0.5);color:white;}`,
  `{background:rgba(0,0,0,0.08);color:#000;}}&[data-severity='strong-informational']{background:#5d5d5d;color:white;}&[data-severity='warning']{background:#f5922f;color:white;}&[data-severity='urgent']{background:#ff4b4b;color:white;}`,
  `{padding:6px;}`
], ActionButton, BannerBadge, mediaMobile);
const CloseButton = styled(ButtonIcon).attrs((p) => {
  return {
    size: "xs",
    ...p
  };
}).withConfig({
  displayName: "BannerDisplay__CloseButton",
  componentId: "sc-85a6a28e-3"
})([
  `color:inherit;width:30px;height:30px;`
]);
const BannerContent = styled(Flex).attrs((p) => {
  return {
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    flexWrap: "wrap",
    ...p
  };
}).withConfig({
  displayName: "BannerDisplay__BannerContent",
  componentId: "sc-85a6a28e-4"
})([
  `word-break:break-word;text-wrap:pretty;`
]);
const isInternalLink = (url) => {
  return url.startsWith("/") || url.startsWith("#");
};
const renderBannerAction = (action) => {
  if (!action) return null;
  switch (action.type) {
    case "url":
      return /* @__PURE__ */ jsx(Link, {
        href: action.url,
        target: action.target ?? "_blank",
        rel: isInternalLink(action.url) ? void 0 : "noopener noreferrer",
        children: /* @__PURE__ */ jsx(ActionButton, {
          children: action.label
        })
      });
    case "refresh":
      return /* @__PURE__ */ jsx(ActionButton, {
        onClick: () => window.location.reload(),
        children: action.label
      });
    default:
      return null;
  }
};
const parseBannerString = (text) => {
  const parts = [];
  let currentIndex = 0;
  while (currentIndex < text.length) {
    const badgeMatch = text.slice(currentIndex).match(/\(\((.*?)\)\)/);
    const variableMatch = text.slice(currentIndex).match(/:([a-zA-Z0-9_]+):/);
    const badgeIndex = badgeMatch ? currentIndex + text.slice(currentIndex).indexOf(badgeMatch[0]) : -1;
    const variableIndex = variableMatch ? currentIndex + text.slice(currentIndex).indexOf(variableMatch[0]) : -1;
    let nextMatchIndex = -1;
    let nextMatchType = null;
    if (badgeIndex !== -1 && variableIndex !== -1) {
      if (badgeIndex < variableIndex) {
        nextMatchIndex = badgeIndex;
        nextMatchType = "badge";
      } else {
        nextMatchIndex = variableIndex;
        nextMatchType = "variable";
      }
    } else if (badgeIndex !== -1) {
      nextMatchIndex = badgeIndex;
      nextMatchType = "badge";
    } else if (variableIndex !== -1) {
      nextMatchIndex = variableIndex;
      nextMatchType = "variable";
    }
    if (nextMatchIndex === -1) {
      const remainingText = text.slice(currentIndex);
      if (remainingText) {
        const html = parseInlineMarkdown(remainingText);
        parts.push({
          type: "text",
          text: html
        });
      }
      break;
    } else if (nextMatchIndex > currentIndex) {
      const html = parseInlineMarkdown(text.slice(currentIndex, nextMatchIndex));
      parts.push({
        type: "text",
        text: html
      });
    }
    if (nextMatchType === "badge" && badgeMatch) {
      const badgeContent = badgeMatch[1];
      parts.push({
        type: "badge",
        content: parseBannerString(badgeContent)
      });
      currentIndex = nextMatchIndex + badgeMatch[0].length;
    } else if (nextMatchType === "variable" && variableMatch) {
      parts.push({
        type: "variable",
        value: variableMatch[1]
      });
      currentIndex = nextMatchIndex + variableMatch[0].length;
    }
  }
  return parts;
};
const renderBannerParts = (parts, countdown) => {
  return parts.map((part, index) => {
    switch (part.type) {
      case "text":
        return /* @__PURE__ */ jsx("span", {
          dangerouslySetInnerHTML: {
            __html: part.text
          }
        }, index);
      case "badge":
        return /* @__PURE__ */ jsx(BannerBadge, {
          children: renderBannerParts(part.content)
        }, index);
      case "variable":
        return /* @__PURE__ */ jsx(Fragment$1, {
          children: part.value === "countdown" ? countdown : part.value
        }, index);
      default:
        return null;
    }
  });
};
const BannerDisplay = ({ alert: banner }) => {
  const dismissBanner = useAlertStore((state) => state.dismissBanner, shallow$1);
  const elementId = `banner-${banner.id}`;
  const [countdown, setCountdown] = useState("");
  const hideAlert = useCallback(() => {
    if (!banner.dismissable) return;
    gsap.to(`#${elementId}`, {
      duration: 0.5,
      opacity: 0,
      onComplete: () => {
        dismissBanner();
      }
    });
  }, [
    banner.dismissable,
    dismissBanner,
    elementId
  ]);
  const parsedTitle = useMemo(() => parseBannerString(banner.title), [
    banner.title
  ]);
  const parsedContent = useMemo(() => banner.content ? parseBannerString(banner.content) : null, [
    banner.content
  ]);
  useClientLayoutEffect(() => {
    if (!banner.countdown) return;
    const interval = setInterval(() => {
      const totalSeconds = dayjs(banner.countdown).diff(dayjs(), "seconds");
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor(totalSeconds % 3600 / 60);
      const seconds = totalSeconds % 60;
      setCountdown(`${String(hours).padStart(2, "0")} ${plur("hour")}, ${String(minutes).padStart(2, "0")} ${plur("minute")}, ${String(seconds).padStart(2, "0")} ${plur("second")}`);
    }, 300);
    return () => clearInterval(interval);
  }, [
    banner.countdown
  ]);
  return /* @__PURE__ */ jsxs(Banner, {
    "data-severity": banner.severity,
    id: elementId,
    children: [
      /* @__PURE__ */ jsx(Tooltip, {
        disabled: !parsedContent,
        label: /* @__PURE__ */ jsx(Fragment, {
          children: parsedContent ? renderBannerParts(parsedContent, countdown) : null
        }),
        children: /* @__PURE__ */ jsxs(BannerContent, {
          children: [
            renderBannerParts(parsedTitle, countdown),
            renderBannerAction(banner.action)
          ]
        })
      }),
      banner.dismissable && /* @__PURE__ */ jsx(CloseButton, {
        onClick: hideAlert,
        children: /* @__PURE__ */ jsx(IconMobileSheet24MiscCross, {})
      })
    ]
  });
};

export { BannerDisplay as default };
//# sourceMappingURL=BannerDisplay.js.map
